/**
 * The trace, as one shared set of sections.
 *
 * The playground grew the richest read of a run — routing, the condition
 * chips, what was established, the order-tags projection, the tools — and the
 * inbox's "Show working" panel had only half of it. Ops analyses REAL tickets
 * in the inbox, so the two screens must show the same working; extracting the
 * sections is what stops them drifting apart again (Deivydas, 2026-09-11).
 *
 * Everything renders from the run row itself: `workflowSlug`/`variantId`/
 * `confidence`/`tags` are columns, and `facts` feeds the same readers the
 * playground uses. No endpoint knows this component exists.
 */

import { useMemo } from 'react';
import { Badge } from '../ui/primitives';
import { cn } from '../../lib/cn';
import { readRunFacts, routingLabel } from '../../lib/ticket/detail';
import { STAMP_PROPERTY_NAMES, readStampPreview } from '../../lib/ticket/stamp';

export interface TraceRunShape {
  workflowSlug: string | null;
  variantId: string | null;
  confidence: number | null;
  tags: string[];
  facts: unknown;
  toolCalls: Array<{ id: string; tool: string; ok: boolean; latencyMs: number }>;
}

export function RunTraceSections({ run }: { run: TraceRunShape }) {
  const facts = useMemo(() => readRunFacts(run.facts), [run.facts]);
  const stamp = useMemo(() => readStampPreview(run.facts), [run.facts]);
  const conditions = useMemo(() => {
    const raw = (run.facts as Record<string, unknown> | null)?.conditions;
    return typeof raw === 'object' && raw !== null && !Array.isArray(raw)
      ? Object.entries(raw as Record<string, boolean>)
      : [];
  }, [run.facts]);

  return (
    <div className="text-xs">
      <section className="border-b border-line px-4 py-3">
        <div className="mb-1.5 flex items-center justify-between">
          <span className="text-ink-500">Routing</span>
          {run.confidence !== null ? (
            <span className="tabular text-ink-400">confidence {run.confidence.toFixed(2)}</span>
          ) : null}
        </div>
        <div className="font-mono text-2xs text-ink-700">
          {routingLabel(run.workflowSlug, run.facts)}
          {run.variantId ? ` · ${run.variantId}` : ''}
        </div>
        {run.tags.length > 0 ? (
          <div className="mt-1.5 flex flex-wrap gap-1">
            {run.tags.map((tag) => (
              <Badge key={tag} tone={tag.startsWith('medical') ? 'stop' : 'neutral'}>
                {tag}
              </Badge>
            ))}
          </div>
        ) : null}
      </section>

      {conditions.length > 0 ? (
        <section className="border-b border-line px-4 py-3">
          <div className="eyebrow mb-1.5">Conditions</div>
          <div className="flex flex-wrap gap-1">
            {conditions.map(([name, value]) => (
              <span
                key={name}
                className={cn(
                  'rounded-sm px-1.5 py-0.5 font-mono text-2xs shadow-hairline',
                  value ? 'bg-ok-100 text-ok-700' : 'bg-surface-sunken text-ink-400',
                )}
              >
                {name} {value ? '✓' : '✗'}
              </span>
            ))}
          </div>
        </section>
      ) : null}

      <section className="border-b border-line px-4 py-3">
        <div className="eyebrow mb-1.5">What was established</div>
        {facts.rows.length === 0 ? (
          <p className="text-ink-400">Nothing was established.</p>
        ) : (
          <dl className="space-y-1.5">
            {facts.rows.map((row) => (
              <div key={row.label} className="flex justify-between gap-3">
                <dt className="text-ink-500">{row.label}</dt>
                <dd
                  className={cn(
                    'text-right',
                    row.tone === 'stop'
                      ? 'text-stop-700'
                      : row.tone === 'held'
                        ? 'text-hold-700'
                        : 'text-ink-700',
                  )}
                >
                  {row.value}
                </dd>
              </div>
            ))}
          </dl>
        )}
      </section>

      {stamp ? (
        <section className="border-b border-line px-4 py-3">
          <div className="eyebrow mb-1.5">Order tags — if the conversation ends here</div>
          {stamp.skipped ? (
            <p className="text-ink-400">Nothing would be stamped: {stamp.skipped}.</p>
          ) : (
            <>
              <dl className="space-y-1.5">
                {STAMP_PROPERTY_NAMES.map((name) => {
                  const value = stamp.properties[name];
                  return (
                    <div key={name} className="flex justify-between gap-3">
                      <dt className="text-ink-500">{name}</dt>
                      <dd className={value ? 'text-right text-ink-700' : 'text-right text-ink-400'}>
                        {value ?? 'nothing'}
                      </dd>
                    </div>
                  );
                })}
              </dl>
              {stamp.tags.length > 0 ? (
                <div className="mt-2 flex flex-wrap gap-1">
                  {stamp.tags.map((tag) => (
                    <Badge key={tag} tone="neutral">
                      {tag}
                    </Badge>
                  ))}
                </div>
              ) : null}
            </>
          )}
        </section>
      ) : null}

      <section className="border-b border-line px-4 py-3">
        <div className="eyebrow mb-1.5">Tools</div>
        {run.toolCalls.length === 0 ? (
          <p className="text-ink-400">No tools were called.</p>
        ) : (
          <ul className="space-y-1">
            {run.toolCalls.map((call) => (
              <li key={call.id} className="flex items-center justify-between">
                <span className="font-mono text-ink-700">{call.tool}</span>
                <span className={call.ok ? 'text-ink-400' : 'text-stop-700'}>
                  {call.ok ? `${call.latencyMs}ms` : 'failed'}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
